import { useState } from 'react';
import { WalletConnect } from './components/WalletConnect';
import { PricingCard } from './components/PricingCard';
import { PaymentFlow } from './components/PaymentFlow';
import { Dashboard } from './components/Dashboard';
import { Docs } from './components/Docs';
import { PromoPricing } from './components/PromoPricing';
import { PromoPaymentFlow } from './components/PromoPaymentFlow';
import { useKleverWallet } from './hooks/useKleverWallet';
import { useConfig, Plan } from './context/ConfigContext';
import { PromoProvider, PromoPlan, PromoToken } from './context/PromoContext';
import './App.css';

type View = 'pricing' | 'payment' | 'dashboard' | 'docs' | 'promo-payment';
type PricingTab = 'api' | 'promo';

function AppContent() {
  const { isConnected } = useKleverWallet();
  const config = useConfig();
  const [view, setView] = useState<View>('pricing');
  const [pricingTab, setPricingTab] = useState<PricingTab>('api');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [duration, setDuration] = useState(1);

  // Promo-specific state
  const [selectedPromoPlan, setSelectedPromoPlan] = useState<PromoPlan | null>(null);
  const [selectedPromoToken, setSelectedPromoToken] = useState<PromoToken | null>(null);
  const [promoDuration, setPromoDuration] = useState(1);

  const handleSelectPlan = (plan: Plan) => {
    setSelectedPlan(plan);
  };

  const handleProceedToPayment = () => {
    if (selectedPlan && isConnected) {
      setView('payment');
    }
  };

  const handlePaymentSuccess = (_txHash: string, _apiKey: string) => {
    setView('dashboard');
    setSelectedPlan(null);
  };

  const handleGoToPricing = () => {
    setView('pricing');
    setSelectedPlan(null);
    setSelectedPromoPlan(null);
    setSelectedPromoToken(null);
  };

  // Promo handlers
  const handlePromoProceedToPayment = (plan: PromoPlan, token: PromoToken, dur: number) => {
    setSelectedPromoPlan(plan);
    setSelectedPromoToken(token);
    setPromoDuration(dur);
    setView('promo-payment');
  };

  const handlePromoPaymentSuccess = () => {
    setView('pricing');
    setPricingTab('promo');
    setSelectedPromoPlan(null);
    setSelectedPromoToken(null);
  };

  // Show loading state while config is loading
  if (config.isLoading) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <span className="logo-icon">📊</span>
            <span className="logo-text">DexScan Plans</span>
          </div>
          <nav className="nav">
            <button
              onClick={() => setView('pricing')}
              className={`nav-link ${view === 'pricing' ? 'active' : ''}`}
            >
              Plans
            </button>
            {isConnected && (
              <button
                onClick={() => setView('dashboard')}
                className={`nav-link ${view === 'dashboard' ? 'active' : ''}`}
              >
                Dashboard
              </button>
            )}
            <button
              onClick={() => setView('docs')}
              className={`nav-link ${view === 'docs' ? 'active' : ''}`}
            >
              Docs
            </button>
          </nav>
          <WalletConnect />
        </div>
      </header>

      <main className={`main ${view === 'docs' ? 'main-full' : ''}`}>
        {view === 'pricing' && (
          <div className="pricing-page">
            {/* Tab Switcher */}
            <div className="pricing-tabs">
              <button
                className={`pricing-tab ${pricingTab === 'api' ? 'active' : ''}`}
                onClick={() => setPricingTab('api')}
              >
                <span className="tab-icon">🔌</span>
                API Access
              </button>
              <button
                className={`pricing-tab ${pricingTab === 'promo' ? 'active' : ''}`}
                onClick={() => setPricingTab('promo')}
              >
                <span className="tab-icon">⭐</span>
                Promote Token
              </button>
            </div>

            {/* API Plans Tab */}
            {pricingTab === 'api' && (
              <>
                <div className="pricing-header">
                  <h1>API Access Plans</h1>
                  <p>
                    Get access to DexScan's powerful API for real-time token data, prices, and
                    analytics on Klever Chain.
                  </p>
                </div>

                <div className="duration-selector">
                  <span>Billing:</span>
                  <div className="duration-options">
                    {[1, 3, 6, 12].map(months => (
                      <button
                        key={months}
                        onClick={() => setDuration(months)}
                        className={`duration-option ${duration === months ? 'selected' : ''}`}
                      >
                        {months === 1 ? 'Monthly' : `${months} Months`}
                        {config.discounts[months] > 0 && (
                          <span className="discount-tag">
                            -{(config.discounts[months] * 100).toFixed(0)}%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pricing-grid">
                  {config.plans.map(plan => (
                    <PricingCard
                      key={plan.id}
                      plan={plan}
                      duration={duration}
                      isSelected={selectedPlan?.id === plan.id}
                      onSelect={() => handleSelectPlan(plan)}
                    />
                  ))}
                </div>

                {selectedPlan && (
                  <div className="checkout-bar">
                    <div className="checkout-summary">
                      <span>
                        Selected: <strong>{selectedPlan.name}</strong> for{' '}
                        <strong>{duration} month{duration > 1 ? 's' : ''}</strong>
                      </span>
                    </div>
                    {isConnected ? (
                      <button onClick={handleProceedToPayment} className="btn btn-primary btn-lg">
                        Proceed to Payment
                      </button>
                    ) : (
                      <div className="connect-prompt">
                        <span>Connect your wallet to continue</span>
                        <WalletConnect />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Promo Tab */}
            {pricingTab === 'promo' && (
              <PromoPricing onProceedToPayment={handlePromoProceedToPayment} />
            )}
          </div>
        )}

        {view === 'payment' && selectedPlan && (
          <div className="payment-page">
            <PaymentFlow
              plan={selectedPlan}
              duration={duration}
              onSuccess={handlePaymentSuccess}
              onCancel={handleGoToPricing}
            />
          </div>
        )}

        {view === 'promo-payment' && selectedPromoPlan && selectedPromoToken && (
          <div className="payment-page">
            <PromoPaymentFlow
              plan={selectedPromoPlan}
              token={selectedPromoToken}
              duration={promoDuration}
              onSuccess={handlePromoPaymentSuccess}
              onCancel={handleGoToPricing}
            />
          </div>
        )}

        {view === 'dashboard' && (
          <Dashboard onUpgrade={handleGoToPricing} />
        )}

        {view === 'docs' && <Docs />}
      </main>

      <footer className="footer">
        <div className="footer-content">
          <p>&copy; {new Date().getFullYear()} Clever Solutions. All rights reserved.</p>
          <div className="footer-links">
            <a href="https://dexscan.me">DexScan</a>
            <button onClick={() => setView('docs')} className="footer-link-btn">Documentation</button>
            <a href="https://t.me/kdexscan">Telegram</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <PromoProvider>
      <AppContent />
    </PromoProvider>
  );
}

export default App;
